import { sheetNamesEqual, sheetRefPrefix } from "./addr.js";
import { parse, readQuotedSheet, tokenize } from "./parser.js";
// Sheet names a formula source references, [] if malformed. Range ends carry no sheet in this grammar, so only the start's counts.
export function referencedSheetNames(src) {
    if (!src.startsWith("="))
        return [];
    let ast;
    try {
        ast = parse(src.slice(1));
    }
    catch {
        return [];
    }
    const names = new Set();
    collect(ast, names);
    return [...names];
}
function collect(ast, out) {
    switch (ast.kind) {
        case "ref":
            if (ast.addr.start.sheet)
                out.add(ast.addr.start.sheet);
            return;
        case "unary":
            collect(ast.operand, out);
            return;
        case "binary":
            collect(ast.left, out);
            collect(ast.right, out);
            return;
        case "call":
            for (const arg of ast.args)
                collect(arg, out);
            return;
        default:
            return;
    }
}
// Every cross-sheet reference, resolved to a target id when the name matches a workbook sheet (null when dangling). Self-refs excluded.
export function sheetReferences(sheet, allSheets) {
    const refs = [];
    for (const [fromCell, cell] of Object.entries(sheet.cells)) {
        for (const sheetName of referencedSheetNames(cell.src)) {
            const target = allSheets.find((s) => sheetNamesEqual(s.name, sheetName));
            if (target && target.id === sheet.id)
                continue;
            refs.push({ fromCell, sheetName, targetId: target?.id ?? null });
        }
    }
    return refs;
}
// Distinct ids of other sheets this one references. Danglers are skipped, they are already broken and cannot be a copy dependency.
export function sheetDependencyIds(sheet, allSheets) {
    const ids = new Set();
    for (const ref of sheetReferences(sheet, allSheets))
        if (ref.targetId)
            ids.add(ref.targetId);
    return [...ids];
}
// Transitive closure of dependencies over the seed ids, including the seeds. Unknown ids are ignored.
export function dependencyClosure(seedIds, allSheets) {
    const byId = new Map(allSheets.map((s) => [s.id, s]));
    const seen = new Set();
    const stack = [...seedIds];
    while (stack.length) {
        const id = stack.pop();
        if (seen.has(id))
            continue;
        const sheet = byId.get(id);
        if (!sheet)
            continue;
        seen.add(id);
        for (const dep of sheetDependencyIds(sheet, allSheets))
            if (!seen.has(dep))
                stack.push(dep);
    }
    return [...seen];
}
// Repoints references from oldName to newName, leaving the rest of the source intact. Used when a copied sheet is renamed on collision.
export function renameSheetRefs(src, oldName, newName) {
    if (!src.startsWith("="))
        return src;
    let tokens;
    try {
        tokens = tokenize(src.slice(1));
    }
    catch {
        return src;
    }
    let out = src;
    for (let i = tokens.length - 1; i >= 0; i--) {
        const t = tokens[i];
        if (t.kind !== "ref" || !t.addr.start.sheet || !sheetNamesEqual(t.addr.start.sheet, oldName))
            continue;
        const tokenStart = t.span.start + 1;
        const tokenText = src.slice(tokenStart, t.span.end + 1);
        const prefixLen = sheetPrefixLength(tokenText);
        if (prefixLen === 0)
            continue;
        out = out.slice(0, tokenStart) + sheetRefPrefix(newName) + out.slice(tokenStart + prefixLen);
    }
    return out;
}
function sheetPrefixLength(tokenText) {
    if (tokenText.startsWith("'"))
        return readQuotedSheet(tokenText, 0)?.afterBang ?? 0;
    const bang = tokenText.indexOf("!");
    return bang >= 0 ? bang + 1 : 0;
}
//# sourceMappingURL=references.js.map